import React, { useEffect, useState } from 'react'
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button,
    CircularProgress,
    FormControl, FormHelperText, IconButton, MenuItem, Modal, Paper, Select, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography
} from '@mui/material'
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from '@mui/icons-material/Close';
import { showErrorToast, showSuccessToast } from '../../../toastUtils';
import { createOrganization, createSubscription, getOrganizationById, updateOrganization } from '../../../api/organization';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../../CommonComponents/Loader';
import ButtonCommon from '../../../CommonComponents/ButtonCommon';
import CreateOrViewLicense from '../LicensePlans/CreateOrViewLicense';
import { getLicenseForSubscription, updateSubscriptionPlan } from '../../../api/license';
import { useUser } from '../../../components/UserContext';
import CustomBreadcrumb from '../../../CommonComponents/CommonBreadcrumb';


export default function CreateOrViewOrganization() {
    // const navigate = useNavigate();
    const { currentUser } = useUser();

    const [formData, setFormData] = useState({
        organizationName: "",
        organizationCategory: "",
        description: "",
        billingName: "",
        billingEmail: "",
        billingPhoneNumber: "",
        distributorName: "",
        partnerName: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        landmark: "",
        buildingName: "",
        officeNumber: ""
    });
    const [initialData, setInitialData] = useState({})
    const [subscription, setSubscription] = useState([])
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [statusValues, setStatusValues] = useState({});
    const { id } = useParams();
    const [loading, setLoading] = useState(id ? true : false);
    const [loadingSubscriptionStatus, setLoadingSubscriptionStatus] = useState(false);
    const [open, setOpen] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOrganization = async () => {
            if (id) {
                try {
                    const response = await getOrganizationById(id);
                    if (response.success) {
                        const orgData = response.data.organization;
                        setInitialData(orgData)
                        setFormData({
                            organizationName: orgData.organization_name || "",
                            organizationCategory: orgData.organization_category || "",
                            description: orgData.description || "",
                            billingName: orgData.full_name || "",
                            billingEmail: orgData.email || "",
                            billingPhoneNumber: orgData.phone || "",
                            distributorName: orgData.distributor_name || "",
                            partnerName: orgData.partner_name || "",
                            country: orgData.billing_address?.country || "",
                            state: orgData.billing_address?.state || "",
                            city: orgData.billing_address?.city || "",
                            pincode: orgData.billing_address?.pincode || "",
                            landmark: orgData.billing_address?.landmark || "",
                            buildingName: orgData.billing_address?.building_name || "",
                            officeNumber: orgData.billing_address?.office_number || ""
                        });
                        setSubscription(response.data.subscriptions || []);
                        setStatusValues(response.data.subscriptions.reduce((acc, curr) => {
                            acc[curr.subscription_id] = curr.status;
                            return acc;
                        }, {}))
                    }
                } catch (error) {
                    console.error("Failed to fetch organization:", error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchOrganization();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: null,
            }));
        }

        setIsEdited(true)
    };

    const validate = () => {
        const newErrors = {};
        const getTrimmed = (val) => String(val ?? '').trim();

        if (!getTrimmed(formData.organizationName)) newErrors.organizationName = "Organization Name is required";
        // if (!getTrimmed(formData.organizationCategory)) newErrors.organizationCategory = "Organization Category is required";

        const description = getTrimmed(formData.description);
        if (!description) newErrors.description = "Description is required";
        // else if (description.length < 10) newErrors.description = "Description must be at least 10 characters";

        if (!getTrimmed(formData.billingName)) newErrors.billingName = "Name is required";

        const email = getTrimmed(formData.billingEmail);
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!email) newErrors.billingEmail = "Email ID is required";
        else if (!emailRegex.test(email)) newErrors.billingEmail = "Invalid email format";

        // if (!getTrimmed(formData.distributorName)) newErrors.distributorName = "Distributor Name is required";
        // if (!getTrimmed(formData.partnerName)) newErrors.partnerName = "Partner Name is required";
        if (!getTrimmed(formData.country)) newErrors.country = "Country is required";
        if (!getTrimmed(formData.state)) newErrors.state = "State is required";
        if (!getTrimmed(formData.city)) newErrors.city = "City is required";

        const phone = getTrimmed(formData.billingPhoneNumber);
        const phoneRegex = /^[0-9]+$/;
        if (!phone) newErrors.billingPhoneNumber = "Phone Number is required";
        else if (!phoneRegex.test(phone)) newErrors.billingPhoneNumber = "Phone Number must be numeric";

        const pincode = getTrimmed(formData.pincode);
        if (!pincode) newErrors.pincode = "Pincode is required";
        else if (!/^\d{6}$/.test(pincode)) newErrors.pincode = "Pincode must be a 6-digit number";

        if (!getTrimmed(formData.landmark)) newErrors.landmark = "Landmark is required";
        if (!getTrimmed(formData.buildingName)) newErrors.buildingName = "Building Name is required";
        // if (!getTrimmed(formData.officeNumber)) newErrors.officeNumber = "Office Number is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubscriptionChange = async (value, subscriptionId) => {
        try {
            setLoadingSubscriptionStatus((prev) => ({ ...prev, [subscriptionId]: true }));

            const payload = { status: value };
            const response = await updateSubscriptionPlan(id, subscriptionId, payload);

            if (response?.success) {
                setStatusValues((prev) => ({
                    ...prev,
                    [subscriptionId]: value,
                }));
                showSuccessToast("Subscription status updated successfully.");
                setSubscription((prev) => prev.map(ele => {
                    if (ele.subscription_id === subscriptionId) {
                        return { ...ele, status: value };  // ✅ Only update status
                    }
                    return ele;
                }));
            } else {
                showErrorToast("Failed to update subscription status.");
            }
        } catch (error) {
            console.error("Error updating subscription status:", error);
            showErrorToast("An error occurred while updating the subscription.");
        } finally {
            setLoadingSubscriptionStatus((prev) => ({ ...prev, [subscriptionId]: false }));
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const payload = {
            full_name: formData.billingName,
            organization_name: formData.organizationName,
            organization_category: formData.organizationCategory,
            email: formData.billingEmail,
            phone: formData.billingPhoneNumber,
            description: formData.description,
            distributor_name: formData.distributorName,
            partner_name: formData.partnerName,
            billing_address: {
                city: formData.city,
                state: formData.state,
                country: formData.country,
                pincode: formData.pincode,
                landmark: formData.landmark,
                building_name: formData.buildingName,
                office_number: formData.officeNumber,
            },
            created_by: currentUser?.zoneinfo,
            custom1: formData.organizationCategory, //organization category
            custom2: formData.distributorName, //distributor
            custom3: formData.partnerName  //partner
        };

        if (id && formData.organizationName === initialData.organization_name) {
            delete payload.organization_name;
        }
        if (id && formData.billingPhoneNumber === initialData.phone) {
            delete payload.phone;
        }

        setIsSaving(true);
        try {
            let response;

            if (id) {
                // Editing existing organization
                response = await updateOrganization(id, payload);
            } else {
                // Creating new organization
                response = await createOrganization(payload);
            }

            if (response?.success) {
                if (id) {
                    showSuccessToast("Organization updated successfully");
                } else {
                    showSuccessToast("Organization created successfully");
                }
                navigate('/customer/organizations');
            } else {
                showErrorToast(response?.message || "Failed to submit organization data.");
            }
        } catch (error) {
            console.error("Organization submission error:", error);
            const serverMsg = error?.response?.data?.message;
            showErrorToast(serverMsg || "Something went wrong. Please try again.");
        } finally {
            setIsSaving(false);
            setIsEdited(false)
        }
    };


    if (loading) {
        return <Loader />
    }


    const breadcrumbItems = [
        {
            text: "Organizations",
            href: "/customer/organizations",
            onClick: () => navigate("/customer/organizations"),
        },
        {
            text: id ? "View Organization" : "Create Organization"
        }
    ];

    const isDisableSubscription = Object.values(statusValues).some(ele => ele === 'active')

    return (
        <Stack>
            {/* common back button */}
            <CustomBreadcrumb
                items={breadcrumbItems}
                onBackClick={() => navigate("/customer/organizations")}
            />
            <Box>
                <Typography variant='h5' fontWeight='bold'>{id ? 'View' : 'Create'} Organization</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 4 }}>
                    <Typography color='#989898' fontSize='14px'>You can {id ? 'view and edit' : 'create'} the organizations on this page.</Typography>
                    <Button
                        variant="outlined"
                        onClick={handleSubmit}
                        disabled={isSaving || (id && !isEdited)}
                        sx={{
                            color: '#000',
                            borderWidth: 2,
                            borderColor: '#FF6348',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#FF6348',
                                color: '#fff'
                            }
                        }}
                    >
                        {id ? isSaving ? 'Updating...' : 'Update' : isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                {/* Organization Details */}
                <Accordion defaultExpanded sx={{ bgcolor: '#F9FAFB', width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{
                            minHeight: '50px',
                            '& .MuiAccordionSummary-content': { margin: '4px 0' },
                        }}>
                        <Typography variant="h6" sx={{ fontSize: '18px' }}>
                            Organization Details
                            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth error={!!errors.organizationName} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Organization Name
                                    <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                </Typography>

                                <TextField
                                    name="organizationName"
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    placeholder="Enter Your Organization Name"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.organizationName && <FormHelperText>{errors.organizationName}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.organizationCategory} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Organization Category
                                </Typography>
                                {/* <TextField
                                    name="organizationCategory"
                                    value={formData.organizationCategory}
                                    onChange={handleChange}
                                    placeholder="Enter Your Organization Category"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                /> */}
                                <Select
                                    name="organizationCategory"
                                    value={formData.organizationCategory}
                                    onChange={handleChange}
                                    size="small"
                                    fullWidth
                                    displayEmpty
                                >
                                    <MenuItem value="FMCG">FMCG</MenuItem>
                                    <MenuItem value="Banking">Banking</MenuItem>
                                    <MenuItem value="Retail">Retail</MenuItem>
                                </Select>
                                {errors.organizationCategory && <FormHelperText>{errors.organizationCategory}</FormHelperText>}
                            </FormControl>
                        </Box>
                        <FormControl fullWidth error={!!errors.description}>
                            <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                Description
                                <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                            </Typography>
                            <TextField
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Enter Your Description"
                                variant="outlined"
                                size="small"
                                multiline
                                minRows={1}  // Minimum 1 row
                                maxRows={3}  // Maximum 3 rows
                                sx={{ bgcolor: '#fff' }}
                            />
                            {errors.description && <FormHelperText>{errors.description}</FormHelperText>}
                        </FormControl>
                    </AccordionDetails>
                </Accordion>

                {/* Billing Admin Details */}
                <Accordion sx={{ bgcolor: '#F9FAFB', width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: '50px', '& .MuiAccordionSummary-content': { margin: '4px 0' } }}>
                        <Typography variant="h6" sx={{ fontSize: '18px' }}>
                            Billing Admin Details
                            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth error={!!errors.billingName} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Name
                                    <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                </Typography>
                                <TextField
                                    name="billingName"
                                    value={formData.billingName}
                                    onChange={handleChange}
                                    placeholder="Enter Your Name"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.billingName && <FormHelperText>{errors.billingName}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.billingEmail} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Email ID
                                    <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                </Typography>
                                <TextField
                                    name="billingEmail"
                                    value={formData.billingEmail}
                                    onChange={handleChange}
                                    placeholder="Enter Your Email ID"
                                    variant="outlined"
                                    size="small"
                                    disabled={id ? true : false}
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.billingEmail && <FormHelperText>{errors.billingEmail}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.billingPhoneNumber} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Phone Number
                                    <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                </Typography>
                                <TextField
                                    name="billingPhoneNumber"
                                    value={formData.billingPhoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*$/.test(value)) {
                                            handleChange(e); // Only allow digits
                                        }
                                    }}
                                    placeholder="Enter Your Phone Number"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.billingPhoneNumber && <FormHelperText>{errors.billingPhoneNumber}</FormHelperText>}
                            </FormControl>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Additional Details */}
                <Accordion sx={{ bgcolor: '#F9FAFB', width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: '50px', '& .MuiAccordionSummary-content': { margin: '4px 0' } }}>
                        <Typography variant="h6" sx={{ fontSize: '18px' }}>
                            Additional Details
                            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth error={!!errors.distributorName} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>Distributor Name</Typography>
                                <TextField
                                    name="distributorName"
                                    value={formData.distributorName}
                                    onChange={handleChange}
                                    placeholder="Enter Your Distributor Name"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.distributorName && <FormHelperText>{errors.distributorName}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.partnerName} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>Partner Name</Typography>
                                <TextField
                                    name="partnerName"
                                    value={formData.partnerName}
                                    onChange={handleChange}
                                    placeholder="Enter Your Partner Name"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.partnerName && <FormHelperText>{errors.partnerName}</FormHelperText>}
                            </FormControl>
                            <FormControl fullWidth error={!!errors.country} sx={{ mb: 2 }}>
                                <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                    Country
                                    <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                </Typography>
                                <TextField
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    placeholder="Enter Country"
                                    variant="outlined"
                                    size="small"
                                    sx={{ bgcolor: '#fff' }}
                                />
                                {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                            </FormControl>
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth error={!!errors.state} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                        State
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    </Typography>
                                    <TextField
                                        name="state"
                                        value={formData.state}
                                        onChange={handleChange}
                                        placeholder="Enter State"
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    {errors.state && <FormHelperText>{errors.state}</FormHelperText>}
                                </FormControl>
                                <FormControl fullWidth error={!!errors.city} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                        City
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    </Typography>
                                    <TextField
                                        name="city"
                                        value={formData.city}
                                        onChange={handleChange}
                                        placeholder="Enter City"
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    {errors.city && <FormHelperText>{errors.city}</FormHelperText>}
                                </FormControl>

                                <FormControl fullWidth error={!!errors.pincode} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                        Pincode
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    </Typography>
                                    <TextField
                                        name="pincode"
                                        value={formData.pincode}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleChange(e); // Only allow digits
                                            }
                                        }}
                                        placeholder="Enter pincode"
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: '#fff' }}
                                        inputProps={{ min: 0 }}
                                    />
                                    {errors.pincode && <FormHelperText>{errors.pincode}</FormHelperText>}
                                </FormControl>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth error={!!errors.landmark} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                        Landmark
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    </Typography>
                                    <TextField
                                        name="landmark"
                                        value={formData.landmark}
                                        onChange={handleChange}
                                        placeholder="Enter landmark"
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    {errors.landmark && <FormHelperText>{errors.landmark}</FormHelperText>}
                                </FormControl>

                                <FormControl fullWidth error={!!errors.buildingName} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>
                                        Building Name
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    </Typography>
                                    <TextField
                                        name="buildingName"
                                        value={formData.buildingName}
                                        onChange={handleChange}
                                        placeholder="Enter building name"
                                        variant="outlined"
                                        size="small"
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    {errors.buildingName && <FormHelperText>{errors.buildingName}</FormHelperText>}
                                </FormControl>

                                <FormControl fullWidth error={!!errors.officeNumber} sx={{ mb: 2 }}>
                                    <Typography gutterBottom sx={{ color: "#5F5F5F" }}>Office Number</Typography>
                                    <TextField
                                        name="officeNumber"
                                        value={formData.officeNumber}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                handleChange(e); // Only allow digits
                                            }
                                        }}
                                        placeholder="Enter office number"
                                        variant="outlined"
                                        size="small"
                                        InputProps={{
                                            disableUnderline: true,
                                            inputProps: {
                                                inputMode: 'numeric',   // Mobile: open numeric keyboard
                                                pattern: '[0-9]*',       // Only numbers
                                            },
                                        }}
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                    {errors.officeNumber && <FormHelperText>{errors.officeNumber}</FormHelperText>}
                                </FormControl>
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Subscription */}
                {id && <Accordion sx={{ bgcolor: '#F9FAFB', width: '100%' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: '50px', '& .MuiAccordionSummary-content': { margin: '4px 0' } }}>
                        <Typography variant="h6" sx={{ fontSize: '18px' }}>Subscription</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end', mb: 2 }}>
                            <ButtonCommon disabled={isDisableSubscription} onClick={() => setOpen(true)}>Attach Subscription</ButtonCommon>
                        </Box>
                        <Box>
                            <TableContainer component={Paper}>
                                <Table size="small" >
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#DFDFDF' }}>
                                            <TableCell>Subscription ID</TableCell>
                                            <TableCell>Plan Name</TableCell>
                                            <TableCell>Expiry Date</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {subscription && subscription.length > 0 ? subscription.map((row) => (
                                            <TableRow
                                                key={row.subscription_id}
                                                hover
                                                sx={{
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                    verticalAlign: 'middle',
                                                }}
                                            >
                                                <TableCell>{row.subscription_key}</TableCell>
                                                <TableCell>{row.plan_details.plan_name}</TableCell>
                                                <TableCell>{row.end_date}</TableCell>
                                                <TableCell
                                                    sx={{
                                                        textTransform: "capitalize",
                                                        color:
                                                            row.status === "active"
                                                                ? "#00B894"
                                                                : row.status === "inactive"
                                                                    ? "#FF946D" : "#6994FF",
                                                        fontWeight: 600,
                                                    }}
                                                >{row.status === 'deactive' ? 'Deactivated' : row.status}</TableCell>
                                                <TableCell sx={{ width: '150px' }}>
                                                    {/* <FormControl fullWidth size="small" variant="outlined">
                                                        {loadingSubscriptionStatus[row.subscription_id] ?
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40px' }}><CircularProgress size={20} /></Box> :
                                                            <Select
                                                                value={statusValues[row.subscription_id]}
                                                                onChange={e => handleSubscriptionChange(e.target.value, row.subscription_id)}
                                                                sx={{ width: '150px' }}
                                                            >
                                                                <MenuItem value="active" disabled={isDisableSubscription}>Active</MenuItem>
                                                                <MenuItem value="inactive">Inactive</MenuItem>
                                                                <MenuItem value="expired">Expired</MenuItem>
                                                                <MenuItem value="deactivated">Deactivated</MenuItem>
                                                            </Select>
                                                        }
                                                    </FormControl> */}
                                                    {loadingSubscriptionStatus[row.subscription_id] ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40px' }}>
                                                            <CircularProgress size={20} />
                                                        </Box>
                                                    ) : (
                                                        <Switch
                                                            disabled={statusValues[row.subscription_id] !== 'active' && isDisableSubscription}
                                                            checked={statusValues[row.subscription_id] === 'active'}
                                                            onChange={(e) => handleSubscriptionChange(e.target.checked ? 'active' : 'deactive', row.subscription_id)}
                                                            sx={{
                                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                                    color: '#FF6348',
                                                                },
                                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                    backgroundColor: '#FF6348',
                                                                },
                                                            }}
                                                        />

                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )) :
                                            (<TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography
                                                        sx={{
                                                            textAlign: 'center',
                                                            py: 2,
                                                            color: 'text.secondary',
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        No data available
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </AccordionDetails>
                </Accordion>}
            </Box>
            <ModalSubscription open={open} onClose={() => setOpen(false)} id={id} subscription={subscription} setSubscription={setSubscription} setStatusValues={setStatusValues} />
        </Stack>
    );
}



const ModalSubscription = ({ open, onClose, id, subscription, setSubscription, setStatusValues }) => {
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [licenseData, setLicenseData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function convertUTCToISTDateOnly(utcDateTimeStr) {
        const utcDate = new Date(utcDateTimeStr + 'Z'); // ensure UTC
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(utcDate.getTime() + istOffset);
        istDate.setHours(0, 0, 0, 0);
        return istDate;
    }

    function getTodayISTMidnight() {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istNow = new Date(now.getTime() + istOffset);
        istNow.setHours(0, 0, 0, 0);
        return istNow;
    }

    useEffect(() => {
        const fetchLicenseData = async () => {
            setLoading(true);
            try {
                const response = await getLicenseForSubscription();
                if (response.success) {
                    const data = response.data.plan_details;

                    const todayIST = getTodayISTMidnight();

                    const filteredData = data.filter((item) => {
                        const expiryIST = convertUTCToISTDateOnly(item.expiry_date);
                        return expiryIST >= todayIST;
                    });

                    setLicenseData(filteredData || []);
                }
            } catch (error) {
                console.error('Error fetching license data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLicenseData();
    }, []);

    // useEffect(() => {
    //     const fetchLicenseData = async () => {
    //         setLoading(true);
    //         try {
    //             const response = await getLicenseForSubscription();
    //             if (response.success) {
    //                 const data = response.data.plan_details

    //                 // const filteredData = data.filter((item) => !subscription.some(sub => sub.plan_details.plan_id === item.plan_id));
    //                 // const filteredData = data.filter((item) => new Date(item.expiry_date) >= new Date());
    //                 const filteredData = data.filter((item) => convertUTCToISTDateOnly(item.expiry_date) >= new Date());
    //                 setLicenseData(filteredData || []);
    //             }
    //         } catch (error) {
    //             console.error('Error fetching license data:', error);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchLicenseData();

    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);

    const handleSave = async () => {
        if (!selectedPlan) {
            showErrorToast('Please select a plan before saving.');
            return;
        }

        const payload = {
            organization_id: id,
            plan_id: selectedPlan
        };

        try {
            setIsSaving(true); // optionally manage loading state
            const response = await createSubscription(payload);

            if (response?.success) {
                const res = await getOrganizationById(id);
                if (res.success) {
                    const subscriptionData = res.data.subscriptions
                    setSubscription(subscriptionData);
                    setStatusValues(subscriptionData.reduce((acc, curr) => {
                        acc[curr.subscription_id] = curr.status;
                        return acc;
                    }, {}))
                }
                showSuccessToast('Subscription created successfully.');
                onClose();
            } else {
                showErrorToast(response?.message || 'Failed to create subscription.');
            }
        } catch (error) {
            console.error('Error creating subscription:', error);
            showErrorToast('An unexpected error occurred.');
        } finally {
            setIsSaving(false);
        }
    };


    return (<>
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    // position: 'absolute',
                    position: 'relative',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    minWidth: 350,
                    maxWidth: 400
                }}
            >
                <Stack gap={3}>
                    <Box>
                        <Typography variant="h6" sx={{ fontSize: 18, textAlign: 'center' }}>
                            Attach Subscription
                        </Typography>
                        {/* Close Icon */}
                        <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                // color: 'grey.500',
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Stack spacing={1}>
                        <Typography variant="body1">Plans</Typography>
                        <Select
                            size="small"
                            fullWidth
                            value={selectedPlan}
                            onChange={(e) => setSelectedPlan(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return 'Select Plan';
                                // Find the plan name that corresponds to the selected plan ID
                                const selectedPlanObj = licenseData.find(plan => plan.plan_id === selected);
                                return selectedPlanObj ? selectedPlanObj.plan_name : 'Select Plan';
                            }}
                        >
                            {loading ? (
                                <MenuItem sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={20} />
                                </MenuItem>
                            ) : (
                                licenseData &&
                                licenseData.length > 0 &&
                                licenseData.map(ele => (
                                    <MenuItem key={ele.plan_id} value={ele.plan_id}>
                                        {ele.plan_name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </Stack>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button variant='outlined' size="small" disabled={isSaving} sx={{ minWidth: 80, maxHeight: 40 }} onClick={() => setCreateModalOpen(true)}>
                            Create
                        </Button>
                        <Button variant='outlined' size="small" disabled={isSaving || !selectedPlan} sx={{ minWidth: 80, maxHeight: 40 }} onClick={handleSave}>
                            {isSaving ? <CircularProgress size={20} /> : 'Attach Subscription'}
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Modal >
        <ModalCreateSubscription open={createModalOpen} onClose={() => setCreateModalOpen(false)} onCloseSubscription={onClose} />
    </>
    );
};



const ModalCreateSubscription = ({ open, onClose, onCloseSubscription }) => {

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    // position: 'absolute',
                    position: 'relative', // Important to position close icon absolutely
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    minWidth: 350,
                    width: '80%',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                }}
            >
                {/* Close Icon */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        // color: 'grey.500',
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Modal Content */}
                <CreateOrViewLicense
                    section="subscription"
                    onClose={onClose}
                    onCloseSubscription={onCloseSubscription}
                />
            </Box>
        </Modal>

    );
};
